import React, { useEffect, useMemo, useState } from 'react';
import {
  createInitialTabData,
  fetchGesTabDataByKey,
  GesTabKey,
  TabData,
  AccessProvisioningStatus,
  GesActionState,
  InspectionModalState,
  createInitialActionState,
  GesActionType,
  getGesActionConfig
} from './helper';
import { InspectionModal } from './InspectionModal';
import { ReservationCard } from './components';
import {
  updateGesReservation,
  uploadGesInspectionImages
} from '../ReservationModule/shared/services/data.service';
import { birdToast } from '../shared/components/core-ui/toast';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';

export default function GESContainer() {
  const [activeTab, setActiveTab] = useState<GesTabKey>('today');
  const [tabData, setTabData] = useState<TabData>(createInitialTabData());
  const [actionStateByReservation, setActionStateByReservation] = useState<
    Record<string, GesActionState>
  >({});
  const [inspectionModal, setInspectionModal] = useState<InspectionModalState | null>(null);

  const updateReservationActionState = (
    reservationId: string,
    updater: (current: GesActionState) => GesActionState
  ) => {
    setActionStateByReservation((prev) => {
      const current = prev[reservationId] || createInitialActionState();
      return {
        ...prev,
        [reservationId]: updater(current)
      };
    });
  };

  const hydrateReservationActions = (reservations: TabData[GesTabKey]['items']) => {
    reservations.forEach((reservation) => {
      updateReservationActionState(reservation.id, (current) => ({
        ...current,
        accessProvisioningStatus:
          (reservation.accessProvisioning as AccessProvisioningStatus) ||
          current.accessProvisioningStatus,
        isUnitInspected: reservation.isUnitInspected ?? current.isUnitInspected,
        inspectionDate: reservation.unitInspectedAt ?? current.inspectionDate
      }));
    });
  };

  const loadTab = async (tabKey: GesTabKey) => {
    setTabData((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        loading: true,
        error: undefined
      }
    }));

    const result = await fetchGesTabDataByKey(tabKey);

    setTabData((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        items: result.items,
        loading: false,
        error: result.error
      }
    }));

    hydrateReservationActions(result.items);
  };

  useEffect(() => {
    loadTab('today');
  }, []);

  const handleTabClick = (tabKey: GesTabKey) => {
    setActiveTab(tabKey);

    if (tabData[tabKey].items.length > 0 || tabData[tabKey].loading) return;
    loadTab(tabKey);
  };

  const tabs: { key: GesTabKey; label: string; count: number }[] = useMemo(
    () => [
      { key: 'today', label: 'Today (Arrivals & Departures)', count: tabData.today.items.length },
      {
        key: 'attention',
        label: 'Attention Required (Overdue)',
        count: tabData.attention.items.length
      },
      {
        key: 'upcoming',
        label: 'Upcoming Arrivals (30 Days)',
        count: tabData.upcoming.items.length
      }
    ],
    [tabData]
  );

  const activeData = tabData[activeTab];
  const showQueueColumn = activeTab === 'today';
  const showTodayActions = activeTab === 'today';
  const showUpcomingActions = activeTab === 'upcoming';

  const getReservationActionState = (reservationId: string) => {
    return actionStateByReservation[reservationId] || createInitialActionState();
  };

  const emitActionPayload = async (
    reservationId: string,
    action: GesActionType,
    payload: Record<string, any>
  ) => {
    const { gesPayload, loadingMessage, successMessage } = getGesActionConfig(action, payload);

    const hasPayload = Object.keys(gesPayload).length > 0;
    if (!hasPayload) return;

    const promise = updateGesReservation(reservationId, gesPayload as any).then((result) => {
      if (isErrorInfo(result)) {
        throw result;
      }
      return result;
    });

    birdToast.promise(promise, {
      loadingMessage,
      successMessage,
      errorFailedMessage: 'Failed to update reservation'
    });
  };

  const handleAccessChange = (reservationId: string, status: AccessProvisioningStatus) => {
    updateReservationActionState(reservationId, (current) => ({
      ...current,
      accessProvisioningStatus: status
    }));

    emitActionPayload(reservationId, 'access_provisioning', {
      accessProvisioningStatus: status
    });
  };

  const handleCheckIn = (reservationId: string, withInspection: boolean) => {
    const now = Date.now();

    updateReservationActionState(reservationId, (current) => ({
      ...current,
      gesCheckInAt: now,
      gesCheckOutAt: null,
      isUnitInspected: withInspection ? current.isUnitInspected : false
    }));

    emitActionPayload(reservationId, withInspection ? 'check_in' : 'check_in_without_inspection', {
      actualCheckInAt: now,
      withInspection
    });
  };

  const handleCheckOut = (reservationId: string) => {
    const now = Date.now();

    updateReservationActionState(reservationId, (current) => ({
      ...current,
      gesCheckOutAt: now
    }));

    emitActionPayload(reservationId, 'check_out', {
      actualCheckOutAt: now
    });
  };

  const handleOpenInspectionDone = (reservationId: string) => {
    setInspectionModal({
      reservationId,
      amenitiesPhoto: null,
      bedPhoto: null,
      toiletPhoto: null,
      livingAreaPhoto: null
    });
  };

  const handleInspectionRejected = (reservationId: string) => {
    updateReservationActionState(reservationId, (current) => ({
      ...current,
      isUnitInspected: false,
      isUnitCleaned: false,
      inspectionDate: Date.now()
    }));

    emitActionPayload(reservationId, 'inspection_rejected', {
      isUnitInspected: false,
      isUnitCleaned: false,
      inspectionDate: Date.now()
    });
  };

  const handleInspectionPhotoChange = (
    photoType: keyof Omit<InspectionModalState, 'reservationId'>,
    file: File | null
  ) => {
    setInspectionModal((prev) =>
      prev
        ? {
            ...prev,
            [photoType]: file
          }
        : prev
    );
  };

  const handleSubmitInspectionDone = () => {
    if (!inspectionModal) return;

    const { reservationId, amenitiesPhoto, bedPhoto, toiletPhoto, livingAreaPhoto } =
      inspectionModal;
    if (!amenitiesPhoto || !bedPhoto || !toiletPhoto || !livingAreaPhoto) return;

    const now = Date.now();

    const promise = uploadGesInspectionImages(reservationId, {
      amenitiesPhoto,
      bedPhoto,
      toiletPhoto,
      livingAreaPhoto
    }).then((uploadResult) => {
      if (isErrorInfo(uploadResult)) {
        throw uploadResult;
      }

      updateReservationActionState(reservationId, (current) => ({
        ...current,
        isUnitInspected: true,
        isUnitCleaned: true,
        inspectionDate: now
      }));

      return updateGesReservation(reservationId, {
        isUnitInspected: true,
        isUnitCleaned: true
      }).then(async (result) => {
        if (isErrorInfo(result)) {
          throw result;
        }

        await loadTab(activeTab);
        setInspectionModal(null);
        return result;
      });
    });

    birdToast.promise(promise, {
      loadingMessage: 'Uploading inspection images...',
      successMessage: 'Inspection completed successfully',
      errorFailedMessage: 'Failed to complete inspection'
    });
  };

  return (
    <div className="min-h-screen bg-creams-50 px-3 py-3 md:px-6 md:py-4">
      <div className="mb-4 flex flex-wrap items-center gap-3 md:mb-5 md:gap-4">
        <h1 className="hidden text-2xl font-recoleta font-semibold text-neutral-500 md:block md:text-3xl">
          Guest Experience Services
        </h1>

        <div className="flex flex-1 flex-wrap items-center justify-center gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabClick(tab.key)}
              className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-gilroy-medium transition-colors md:px-4 md:py-2.5 md:text-lg ${
                activeTab === tab.key
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-white text-neutral-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className="ml-2 inline-flex size-6 items-center justify-center rounded-full bg-white/80 px-1.5 text-sm font-semibold font-gilroy-medium md:size-7 md:text-base">
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {activeData.loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-center text-lg text-neutral-500 shadow-sm md:py-10 md:text-2xl">
          Loading reservations...
        </div>
      ) : activeData.items.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-8 text-center text-lg text-neutral-500 shadow-sm md:py-10 md:text-2xl">
          No reservations in this tab.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 2xl:grid-cols-3">
          {activeData.items.map((item, index) => (
            <ReservationCard
              key={`${item.id}-${item.queueType}-${index}`}
              item={item}
              showQueue={showQueueColumn}
              showTodayActions={showTodayActions}
              showUpcomingActions={showUpcomingActions}
              actionState={getReservationActionState(item.id)}
              onAccessChange={handleAccessChange}
              onCheckIn={handleCheckIn}
              onCheckOut={handleCheckOut}
              onInspectionDone={handleOpenInspectionDone}
              onInspectionRejected={handleInspectionRejected}
            />
          ))}
        </div>
      )}

      {inspectionModal && (
        <InspectionModal
          inspectionModal={inspectionModal}
          onPhotoChange={handleInspectionPhotoChange}
          onSubmit={handleSubmitInspectionDone}
          onCancel={() => setInspectionModal(null)}
        />
      )}
    </div>
  );
}
